import React, { useEffect, useMemo, useState } from "react";
import { Card, Donut, EmptyState, ProgressBar, SectionHeading, Segmented } from "../ui";
import { Award, Trophy } from "../icons";
import { getAnswers } from "../../services/AssessmentService";
import { summarisePlan } from "../../lib/learning";

/**
 * "Achievements & progress" — the same numbers the old dashboard showed, read
 * in one pass instead of four.
 *
 * Two halves, deliberately different weights: the learner's own completion is a
 * single filled donut on the accent surface (it's about them), and scores are a
 * quiet ranked list on white (it's a reference table).
 *
 * The Me / Overall switch scopes the SCORES only, because that's the one place
 * the API genuinely has both: `myScore` is this learner's best per assessment,
 * `totalScore` is the organisation's top scorers per assessment. Completion
 * progress is personal by definition, so it doesn't pretend to have an
 * organisation-wide view.
 */

/* On the indigo surface the status hues need lifting to stay legible. */
const DONUT_COLORS = {
  completed: "#3fc98a",
  "in-progress": "#f5b642",
  assigned: "rgba(255,255,255,.32)",
};

const LEGEND = [
  { key: "completed", label: "Completed" },
  { key: "in-progress", label: "In progress" },
  { key: "assigned", label: "Not started" },
];

const OverallProgressCard = ({ summary, highestScore }) => (
  <div
    className="ds-card"
    style={{
      background: "var(--brand-gradient)",
      border: "none",
      color: "#fff",
      padding: "var(--s-5)",
    }}
  >
    <h3 className="ds-h3" style={{ color: "#fff" }}>
      Overall progress
    </h3>
    <p className="ds-sub" style={{ color: "rgba(255,255,255,.7)" }}>
      Across everything assigned to you.
    </p>

    <div
      className="ds-row-flex ds-gap-5 ds-mt-5"
      style={{ alignItems: "center" }}
    >
      <Donut
        size={128}
        thickness={14}
        slices={LEGEND.map((l) => ({
          value: summary[l.key] || 0,
          color: DONUT_COLORS[l.key],
        }))}
        centre={
          <span style={{ textAlign: "center", color: "#fff" }}>
            <span style={{ fontSize: 25, fontWeight: 700, letterSpacing: "-.02em" }}>
              {summary.percentComplete}%
            </span>
            <span
              style={{
                display: "block",
                fontSize: 11,
                fontWeight: 500,
                color: "rgba(255,255,255,.72)",
                marginTop: 2,
              }}
            >
              completed
            </span>
          </span>
        }
      />

      <div className="ds-grow">
        {LEGEND.map((l) => (
          <div
            key={l.key}
            className="ds-row-flex ds-between ds-gap-3"
            style={{
              padding: "7px 0",
              borderBottom: "1px solid rgba(255,255,255,.12)",
            }}
          >
            <span className="ds-row-flex ds-gap-2" style={{ fontSize: 13 }}>
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 999,
                  background: DONUT_COLORS[l.key],
                  display: "inline-block",
                }}
              />
              {l.label}
            </span>
            <span style={{ fontSize: 14, fontWeight: 650 }}>
              {summary[l.key] || 0}
            </span>
          </div>
        ))}

        <div
          className="ds-row-flex ds-between ds-gap-3"
          style={{ padding: "9px 0 0" }}
        >
          <span
            className="ds-row-flex ds-gap-2"
            style={{ fontSize: 13, color: "rgba(255,255,255,.82)" }}
          >
            <Award size={14} />
            Highest module score
          </span>
          <span style={{ fontSize: 14, fontWeight: 650 }}>
            {highestScore != null ? `${Math.round(highestScore)}%` : "—"}
          </span>
        </div>
      </div>
    </div>
  </div>
);

const ScoreRow = ({ name, value, caption }) => (
  <div className="ds-row">
    <span className="ds-tile" style={{ width: 28, height: 28, borderRadius: 8 }}>
      <Award size={14} />
    </span>
    <span className="ds-grow" style={{ minWidth: 0 }}>
      <span className="ds-h3 ds-truncate" style={{ fontSize: 13, display: "block" }}>
        {name}
      </span>
      {caption && (
        <span style={{ fontSize: 11.5, color: "var(--ink-3)" }}>{caption}</span>
      )}
    </span>
    <span style={{ width: 128, flexShrink: 0 }}>
      <ProgressBar value={value} tone={value >= 80 ? "success" : undefined} />
    </span>
    <span className="ds-bar__value">{Math.round(value)}%</span>
  </div>
);

export const AchievementsPanel = ({ planItems = [] }) => {
  const [scope, setScope] = useState("me");
  const [myScore, setMyScore] = useState([]);
  const [teamScore, setTeamScore] = useState([]);

  useEffect(() => {
    (async () => {
      const response = await getAnswers();
      if (response?.data) {
        setMyScore(response.data.myScore || []);
        setTeamScore(response.data.totalScore || []);
      }
    })();
  }, []);

  const summary = useMemo(() => summarisePlan(planItems), [planItems]);

  const highestScore = useMemo(
    () =>
      myScore.length
        ? Math.max(...myScore.map((s) => Number(s.percentage) || 0))
        : null,
    [myScore]
  );

  // Best attempt per assessment, strongest first — a ranking, not a log.
  const myBest = useMemo(() => {
    const best = new Map();
    myScore.forEach((s) => {
      const name = s.assessment?.name;
      if (!name) return;
      const pct = Number(s.percentage) || 0;
      if (!best.has(name) || best.get(name) < pct) best.set(name, pct);
    });
    return [...best.entries()]
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [myScore]);

  return (
    <section className="ds-section">
      <SectionHeading
        title="Achievements & progress"
        description="A clearer view of how you're doing — same data, better read."
      />

      <div className="ds-grid ds-grid--2">
        <OverallProgressCard summary={summary} highestScore={highestScore} />

        <Card>
          <div className="ds-card__head">
            <div>
              <h3 className="ds-h3">Your best scores</h3>
              <p className="ds-sub" style={{ marginTop: 2 }}>
                {scope === "me"
                  ? "Highest score per module"
                  : "Top scorers per module"}
              </p>
            </div>
            <Segmented
              value={scope}
              onChange={setScope}
              options={[
                { value: "me", label: "Me" },
                { value: "overall", label: "Overall" },
              ]}
            />
          </div>

          <div
            className="ds-card__body ds-scroll"
            style={{ maxHeight: 288, overflowY: "auto", paddingTop: 0 }}
          >
            {scope === "me" ? (
              myBest.length ? (
                myBest.map((s) => (
                  <ScoreRow key={s.name} name={s.name} value={s.value} />
                ))
              ) : (
                <EmptyState
                  icon={Trophy}
                  text="Attempt an assessment or warm up and your best scores will appear here."
                />
              )
            ) : teamScore.length ? (
              teamScore.map((module, i) => (
                <div key={module._id?.name || i} style={{ paddingTop: i ? 14 : 0 }}>
                  <p className="ds-eyebrow" style={{ marginBottom: 2 }}>
                    {module._id?.name || "Unknown module"}
                  </p>
                  {(module.topScores || []).map((score, idx) => (
                    <ScoreRow
                      key={score._id || idx}
                      name={score.user?.userName || "Unknown user"}
                      caption={`Rank ${idx + 1}`}
                      value={Number(score.percentage) || 0}
                    />
                  ))}
                </div>
              ))
            ) : (
              <EmptyState
                icon={Trophy}
                text="No assessment scores have been recorded across the team yet."
              />
            )}
          </div>
        </Card>
      </div>
    </section>
  );
};

export default AchievementsPanel;
